/**
 * V2 Lists — the data module (M8, net-new; no V1 counterpart).
 *
 * "Want to go" is a per-user system list (kind='want_to_go') behind the
 * save button on place pages — works like Google Maps saves, per the
 * familiarity rules. Custom lists add notes, ordering, and visibility
 * (private / unlisted / public); slugs are unique per owner, not
 * globally (V2-PLAN §4-5).
 *
 * Store slices owned here: wantToGoListId, savedLocationIds (the
 * want-to-go membership, loaded lazily on the first page that needs the
 * save button — not on login), listsUnavailable (schema not applied yet
 * → save UI hides instead of erroring).
 */
import * as gateway from '../../core/gateway.js';
import { cached, invalidate } from '../../core/query.js';
import { drainRows } from '../../core/drain.js';
import { slugify } from '../../../assets/js/shared/slugify.js';
import { haversineKm } from '../category/category-stats.js';

const LIST_SELECT = '*,list_entries(count)';
// Entry reads DRAIN in keyset batches (2026-08-04: a real 917-place
// "Want to go" import sailed past the old flat 500 cap — the list page
// showed "500 places" while My lists honestly counted 917).
const ENTRY_BATCH = 500;

export const WANT_TO_GO = { kind: 'want_to_go', title: 'Want to go', slug: 'want-to-go' };

function entryCount(list) {
  return list?.list_entries?.[0]?.count ?? 0;
}

/** All my lists, want-to-go first, then custom by age. Cached per user. */
export async function fetchMyLists(store) {
  const me = store.get().user;
  if (!me) return [];
  return cached(`my-lists:${me.id}`, async () => {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.LISTS, {
      select: LIST_SELECT,
      filters: { owner_id: `eq.${me.id}` },
      order: 'created_at.asc',
      limit: 200,
    });
    if (error) throw error;
    const lists = (data || []).map((l) => ({ ...l, entry_count: entryCount(l) }));
    return [
      ...lists.filter((l) => l.kind === WANT_TO_GO.kind),
      ...lists.filter((l) => l.kind !== WANT_TO_GO.kind),
    ];
  });
}

/**
 * Load the want-to-go membership into the store so save buttons render
 * instantly everywhere. Lazy: call from views that show a save button.
 * Degrades to "lists unavailable" while the M8 schema isn't applied yet.
 */
let savedLoading = false;
export async function loadSaved(store) {
  const state = store.get();
  if (!state.user || savedLoading || state.savedLocationIds || state.listsUnavailable) return;
  savedLoading = true;
  try {
    const lists = await fetchMyLists(store);
    const wtg = lists.find((l) => l.kind === WANT_TO_GO.kind) || null;
    let saved = [];
    if (wtg) {
      // list_entries has NO id column (composite PK) — location_id is
      // unique within one list, so it cursors the drain
      const rows = await drainRows(gateway.TABLES.LIST_ENTRIES, {
        select: 'location_id',
        filters: { list_id: `eq.${wtg.id}` },
        uniqueKey: 'location_id',
        batch: ENTRY_BATCH,
      });
      saved = rows.map((row) => row.location_id);
    }
    store.set({ wantToGoListId: wtg?.id || null, savedLocationIds: saved, listsUnavailable: false });
  } catch {
    // Schema not applied yet (or offline) — hide the save UI, don't break the page
    store.set({ wantToGoListId: null, savedLocationIds: null, listsUnavailable: true });
  } finally {
    savedLoading = false;
  }
}

export function isSaved(store, locationId) {
  return (store.get().savedLocationIds || []).includes(locationId);
}

/** How far "near you" reaches (the header proximity light). City-and-a-
 * day-trip scale on purpose — tighter than the category card's 100km,
 * because an ambient light that stays lit for a place 90km away trains
 * people to ignore it. */
export const NEARBY_RANGE_KM = 50;

/**
 * The want-to-go entries WITH their places' coordinates — loadSaved's
 * id-only membership can't say how far anything is. Cached under
 * `list-entries:{id}`, a key every add/remove/cross-off path already
 * invalidates, so the proximity light recounts after any change.
 * Resolves [] whenever there's nothing to measure: signed out, lists
 * schema not applied, or no list yet.
 */
export async function fetchSavedPlaces(store) {
  await loadSaved(store);
  const listId = store.get().wantToGoListId;
  if (!listId || store.get().listsUnavailable) return [];
  return cached(`list-entries:${listId}`, async () => drainRows(gateway.TABLES.LIST_ENTRIES, {
    select: '*,locations(id,display_name,formatted_address,lat,lng,status)',
    filters: { list_id: `eq.${listId}` },
    uniqueKey: 'location_id', // composite PK — no id column
    batch: ENTRY_BATCH,
  }));
}

/**
 * The entries within range of HERE — a real GPS fix, never the map's
 * panned center — nearest first. Entries whose place has no coordinates
 * can't be measured, so they don't count. A permanently-closed place is
 * not somewhere to send anyone, so it never lights the header (2026-08-03);
 * temporarily closed still counts — Google's temporary flag goes stale for
 * years, and the place page shows the stamp either way. Pure; exported
 * for tests.
 */
export function savedNearby(entries, here, limitKm = NEARBY_RANGE_KM) {
  if (!here) return [];
  const out = [];
  for (const entry of entries || []) {
    const loc = entry.locations;
    if (typeof loc?.lat !== 'number' || typeof loc?.lng !== 'number') continue;
    if (loc.status === 'closed_permanently') continue;
    const km = haversineKm(here, loc);
    if (km <= limitKm) out.push({ ...entry, km });
  }
  return out.sort((a, b) => a.km - b.km);
}

/**
 * Find or create my "Want to go" system list. Never fails silently: a
 * dead save button that says nothing is how the 2026-07-09 on-device
 * report read ("want to go button did nothing").
 */
export async function ensureWantToGo(store) {
  const me = store.get().user;
  if (!me) return null;
  const existing = store.get().wantToGoListId;
  if (existing) return existing;
  invalidate('my-lists:'); // the cache may predate a hand-made list
  const lists = await fetchMyLists(store);
  let wtg = lists.find((l) => l.kind === WANT_TO_GO.kind);
  if (!wtg) {
    // A hand-made custom list can already own the want-to-go slug (the
    // owner+slug unique would 409 the system insert forever) — adopt it
    // as the system list instead of shadowing it with a -2 twin.
    const handmade = lists.find((l) => l.slug === WANT_TO_GO.slug);
    if (handmade) {
      await updateList(handmade.id, { kind: WANT_TO_GO.kind });
      wtg = { ...handmade, kind: WANT_TO_GO.kind };
    }
  }
  if (!wtg) {
    const { data, error } = await gateway.rawInsert(gateway.TABLES.LISTS, {
      body: { owner_id: me.id, ...WANT_TO_GO },
    });
    // 409 = created on another device in between — refetch and converge
    if (error && !/HTTP 409/.test(error.message)) throw error;
    invalidate('my-lists:');
    wtg = error ? (await fetchMyLists(store)).find((l) => l.kind === WANT_TO_GO.kind) : data?.[0];
  }
  if (!wtg) throw new Error('Couldn’t set up your Want to go list.');
  store.set({ wantToGoListId: wtg.id });
  return wtg.id;
}

/** Add a place to a list (double-tap converges on 409, like follows). */
/**
 * Returns true when a row was actually inserted, false when the list already
 * held that location. The 409 has always been swallowed — (list_id,
 * location_id) is the primary key — but callers used to read "no throw" as
 * "added", so a bulk import could report 83 added and grow the list by 78
 * (report 2026-08-27). Saying which happened is the caller's business now.
 */
export async function addToList(listId, locationId, note = null) {
  const { error } = await gateway.rawInsert(gateway.TABLES.LIST_ENTRIES, {
    body: { list_id: listId, location_id: locationId, note },
  });
  const duplicate = !!error && /HTTP 409/.test(error.message);
  if (error && !duplicate) throw error;
  invalidateListCaches(listId);
  return !duplicate;
}

export async function removeFromList(listId, locationId) {
  const { error } = await gateway.rawDelete(gateway.TABLES.LIST_ENTRIES, {
    filters: { list_id: `eq.${listId}`, location_id: `eq.${locationId}` },
  });
  if (error) throw error;
  invalidateListCaches(listId);
}

/**
 * Every cache that carries list titles or entry counts. public-lists:
 * feeds the profile's Lists strip — missing it left a stale "3 places"
 * after removals (on-device report 2026-07-09).
 */
function invalidateListCaches(listId = null) {
  if (listId) {
    invalidate(`list-entries:${listId}`);
    invalidate(`list-visits:${listId}`); // membership changed → progress recounts
  }
  invalidate('my-lists:');
  invalidate('list-page:');
  invalidate('public-lists:');
  // Category surfaces (search results + the category page's shelf) —
  // an edit that recategorizes a list must not leave either stale
  invalidate('list-search:');
  invalidate('category-lists:');
}

/** The "Want to go" toggle behind the place-page save button. */
export async function saveWantToGo(store, locationId) {
  const listId = await ensureWantToGo(store);
  if (!listId) throw new Error('Sign in to save places.');
  await addToList(listId, locationId);
  store.set({ savedLocationIds: [...new Set([...(store.get().savedLocationIds || []), locationId])] });
}

export async function unsaveWantToGo(store, locationId) {
  const listId = store.get().wantToGoListId;
  if (!listId) return;
  await removeFromList(listId, locationId);
  store.set({ savedLocationIds: (store.get().savedLocationIds || []).filter((id) => id !== locationId) });
}

/**
 * You went — cross it off. Called after a yoink is logged: if the place
 * sits on MY "Want to go" list, take it off. ONLY the system list —
 * custom lists ("Top ten burgers") keep the place; going doesn't change
 * what a curated list means, but it completes "want to go".
 * Never creates the list, and returns true only when an entry actually
 * came off (the caller's cue to say so).
 */
export async function crossOffWantToGo(store, locationId) {
  const state = store.get();
  if (!state.user || state.listsUnavailable || !locationId) return false;
  // The log page doesn't show save buttons, so the membership may not be
  // loaded yet — load it here rather than deleting blind on every yoink.
  if (!state.savedLocationIds) await loadSaved(store);
  if (!store.get().wantToGoListId || !isSaved(store, locationId)) return false;
  await unsaveWantToGo(store, locationId);
  return true;
}

/**
 * "Want to go" means you HAVEN'T been — going takes the place off the
 * list (user rule 2026-08-06; same philosophy as crossOffWantToGo).
 * The log flow crosses off live; this sweep catches everything it
 * can't: backdated logs, imports of places already visited, V1-era
 * yoinks. Batch-deleted in in.(…) chunks (same URL bound as
 * fetchListVisits), then the usual cache/slice upkeep. ONLY ever
 * called for the system list, never a custom one.
 */
export async function sweepVisitedFromWantToGo(store, listId, visitedLocationIds) {
  const ids = [...visitedLocationIds].filter(Boolean);
  if (!ids.length) return 0;
  for (let i = 0; i < ids.length; i += VISITS_CHUNK) {
    const chunk = ids.slice(i, i + VISITS_CHUNK);
    const { error } = await gateway.rawDelete(gateway.TABLES.LIST_ENTRIES, {
      filters: { list_id: `eq.${listId}`, location_id: `in.(${chunk.join(',')})` },
    });
    if (error) throw error;
  }
  invalidateListCaches(listId);
  if (store.get().wantToGoListId === listId) {
    const gone = new Set(ids);
    store.set({
      savedLocationIds: (store.get().savedLocationIds || []).filter((id) => !gone.has(id)),
    });
  }
  return ids.length;
}

/**
 * "URL Link" free-typing → something clickable, same treatment the yoink
 * form gives its "Link to your post" (log.js). Empty stays null.
 */
export function normalizeUrl(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * Create a custom list. Slug comes from the title; a per-owner slug
 * collision retries with -2, -3… (two users can both have
 * "top-ten-burgers"; one user can't).
 */
export async function createList(store, { title, description = null, visibility = 'private', url = null, categoryId = null }) {
  const me = store.get().user;
  if (!me) return null;
  const base = slugify(title) || 'list';
  for (let attempt = 0; attempt < 5; attempt++) {
    const slug = attempt === 0 ? base : `${base}-${attempt + 1}`;
    const { data, error } = await gateway.rawInsert(gateway.TABLES.LISTS, {
      // url and category_id ride only when set, so creates keep working
      // before their migrations
      body: {
        owner_id: me.id, title, slug, description, visibility,
        ...(url ? { url } : {}),
        ...(categoryId ? { category_id: categoryId } : {}),
      },
    });
    if (!error) {
      invalidateListCaches();
      return data?.[0] || null;
    }
    if (!/HTTP 409/.test(error.message)) throw error;
  }
  throw new Error('That list name is taken — try another.');
}

export async function updateList(listId, patch) {
  const { data, error } = await gateway.rawUpdate(gateway.TABLES.LISTS, {
    filters: { id: `eq.${listId}` },
    body: patch,
  });
  if (error) throw error;
  invalidateListCaches();
  return data?.[0] || null;
}

export async function deleteList(store, listId) {
  const { error } = await gateway.rawDelete(gateway.TABLES.LISTS, {
    filters: { id: `eq.${listId}` },
  });
  if (error) throw error;
  invalidateListCaches(listId);
  if (store.get().wantToGoListId === listId) {
    store.set({ wantToGoListId: null, savedLocationIds: [] });
  }
}

export async function updateEntryNote(listId, locationId, note) {
  const { error } = await gateway.rawUpdate(gateway.TABLES.LIST_ENTRIES, {
    filters: { list_id: `eq.${listId}`, location_id: `eq.${locationId}` },
    body: { note: note || null },
  });
  if (error) throw error;
  invalidate(`list-entries:${listId}`);
  invalidate('list-page:');
}

/**
 * Move an entry one step up or down. Positions normalize to 0..n-1 on
 * the way through, so only rows whose position actually changed get a
 * PATCH — a swap in an already-ordered list is exactly two writes.
 * Returns the reordered entries array for immediate re-render.
 */
export async function moveEntry(listId, entries, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= entries.length) return entries;
  const next = [...entries];
  [next[index], next[target]] = [next[target], next[index]];
  const writes = [];
  const reordered = next.map((entry, i) => {
    if (entry.position !== i) {
      writes.push(gateway.rawUpdate(gateway.TABLES.LIST_ENTRIES, {
        filters: { list_id: `eq.${listId}`, location_id: `eq.${entry.location_id}` },
        body: { position: i },
      }));
    }
    return { ...entry, position: i };
  });
  const results = await Promise.all(writes);
  const failed = results.find((r) => r.error);
  if (failed) throw failed.error;
  invalidate(`list-entries:${listId}`);
  invalidate('list-page:');
  return reordered;
}

/**
 * A public/unlisted list page's data: owner profile, the list, its
 * entries with their places, in position order. Returns null when the
 * URL doesn't resolve (no such user/list, or private and not yours —
 * RLS makes those indistinguishable on purpose).
 */
export async function fetchListPage(username, slug) {
  return cached(`list-page:${username.toLowerCase()}:${slug}`, async () => {
    const { data: profiles, error: profileError } = await gateway.rawQuery(gateway.TABLES.PUBLIC_PROFILES, {
      select: 'id,username,full_name,avatar_url',
      filters: { username: `ilike.${username}` },
      limit: 1,
    });
    if (profileError) throw profileError;
    const owner = profiles?.[0];
    if (!owner) return null;

    const { data: lists, error: listError } = await gateway.rawQuery(gateway.TABLES.LISTS, {
      select: '*',
      filters: { owner_id: `eq.${owner.id}`, slug: `eq.${slug}` },
      limit: 1,
    });
    if (listError) throw listError;
    const list = lists?.[0];
    if (!list) return null;

    // Each place rides with its newest yoink photo (PostgREST embedded
    // order/limit — one query, one row per place) and its Google photo
    // URL from the metadata JSONB, so the map popups have faces even
    // for places nobody has yoinked yet. Drained in keyset batches
    // (location_id cursor — the composite PK has no id column), then
    // sorted client-side into the list's own order — the cursor and
    // position orders can't share one query.
    let entries;
    try {
      entries = await drainRows(gateway.TABLES.LIST_ENTRIES, {
        select: '*,locations(id,display_name,formatted_address,lat,lng,google_place_id,status,photo_url:metadata->>photo_url,yoinks(images,created_at))',
        filters: {
          list_id: `eq.${list.id}`,
          'locations.yoinks.order': 'created_at.desc',
          'locations.yoinks.limit': '1',
        },
        uniqueKey: 'location_id',
        batch: ENTRY_BATCH,
      });
    } catch {
      // The photo embellishment must never cost the page itself —
      // fall back to the plain shape if the richer select is refused
      entries = await drainRows(gateway.TABLES.LIST_ENTRIES, {
        select: '*,locations(id,display_name,formatted_address,lat,lng)',
        filters: { list_id: `eq.${list.id}` },
        uniqueKey: 'location_id',
        batch: ENTRY_BATCH,
      });
    }
    const pos = (e) => (e.position == null ? Infinity : e.position);
    entries.sort((a, b) => pos(a) - pos(b)
      || String(a.added_at || '').localeCompare(String(b.added_at || '')));
    return { owner, list, entries };
  }, { ttl: 15_000 });
}

/**
 * Which of a list's places the viewer has yoinked — their progress
 * through it. Derived from yoinks, never stored (standing decision 1,
 * docs/DEFAULT-APP-2026-08.md): a backdated "Date yoinked" counts by
 * construction, so a visit from before the list existed still fills the
 * bar. Chunked in.(…) keeps each URL bounded (a 500-entry import would
 * otherwise build an ~18KB query string). Returns a Set of location ids;
 * empty when signed out or the list is empty.
 */
const VISITS_CHUNK = 100;
export async function fetchListVisits(store, listId, locationIds) {
  const me = store.get().user;
  const ids = (locationIds || []).filter(Boolean);
  if (!me || !ids.length) return new Set();
  return cached(`list-visits:${listId}:${me.id}`, async () => {
    const chunks = [];
    for (let i = 0; i < ids.length; i += VISITS_CHUNK) {
      chunks.push(ids.slice(i, i + VISITS_CHUNK));
    }
    const results = await Promise.all(chunks.map((chunk) =>
      gateway.rawQuery(gateway.TABLES.YOINKS, {
        select: 'location_id',
        filters: { user_id: `eq.${me.id}`, location_id: `in.(${chunk.join(',')})` },
        limit: chunk.length,
      })));
    const failed = results.find((r) => r.error);
    if (failed) throw failed.error;
    // Personal yoinks count by construction (§11): they're rows in
    // yoinks like any other, so no second table to union anymore.
    return new Set(results.flatMap((r) => (r.data || []).map((row) => row.location_id)));
  }, { ttl: 15_000 });
}

// ---------------------------------------------------------------------------
// List follows — someone else's shared list on your own /lists shelf.
// The table may not be applied yet (no migration pipeline), so every
// reader returns null for "unavailable" and the UI hides rather than errors.
// ---------------------------------------------------------------------------

/** The list ids I follow, or null while signed out / schema not applied. */
export async function fetchMyListFollowIds(store) {
  const me = store.get().user;
  if (!me) return null;
  try {
    return await cached(`list-follows:${me.id}`, async () => {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.LIST_FOLLOWS, {
        select: 'list_id',
        filters: { user_id: `eq.${me.id}` },
        limit: 200,
      });
      if (error) throw error;
      return (data || []).map((row) => row.list_id);
    });
  } catch {
    return null;
  }
}

/**
 * The lists I follow, each with its entry count and owner (username is
 * the URL key). A follow whose list went private or away reads as null
 * through RLS and drops out silently — the shelf shows what you can
 * still open. Returns null when unavailable (signed out / no table yet).
 */
export async function fetchFollowedLists(store) {
  const me = store.get().user;
  if (!me) return null;
  try {
    return await cached(`followed-lists:${me.id}`, async () => {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.LIST_FOLLOWS, {
        select: `list_id,lists(${LIST_SELECT})`,
        filters: { user_id: `eq.${me.id}` },
        order: 'created_at.asc',
        limit: 200,
      });
      if (error) throw error;
      const lists = (data || [])
        .map((row) => row.lists)
        .filter(Boolean)
        .map((l) => ({ ...l, entry_count: entryCount(l) }));
      return joinOwners(lists);
    });
  } catch {
    return null;
  }
}

/** Follow (double-tap converges on 409, same as user/category follows). */
export async function followList(store, listId) {
  const me = store.get().user;
  if (!me) throw new Error('Sign in to follow lists.');
  const { error } = await gateway.rawInsert(gateway.TABLES.LIST_FOLLOWS, {
    body: { user_id: me.id, list_id: listId },
  });
  if (error && !/HTTP 409/.test(error.message)) throw error;
  invalidate('list-follows:');
  invalidate('followed-lists:');
}

export async function unfollowList(store, listId) {
  const me = store.get().user;
  if (!me) return;
  const { error } = await gateway.rawDelete(gateway.TABLES.LIST_FOLLOWS, {
    filters: { user_id: `eq.${me.id}`, list_id: `eq.${listId}` },
  });
  if (error) throw error;
  invalidate('list-follows:');
  invalidate('followed-lists:');
}

/**
 * Which of the given lists already hold this place (the checkboxes in
 * the "Save to list" dialog). listIds is my own lists — bounded, so the
 * in.(…) stays within scale rule 3.
 */
export async function fetchMembership(locationId, listIds) {
  if (!listIds.length) return [];
  const { data, error } = await gateway.rawQuery(gateway.TABLES.LIST_ENTRIES, {
    select: 'list_id',
    filters: { location_id: `eq.${locationId}`, list_id: `in.(${listIds.join(',')})` },
    limit: listIds.length,
  });
  if (error) throw error;
  return (data || []).map((row) => row.list_id);
}

/**
 * Owners joined flat (never an FK embed — an embed of a missing FK 400s
 * the whole query). username is the list URL's key, so a list whose
 * owner has no public profile has no reachable URL — drop those.
 */
async function joinOwners(lists) {
  const ownerIds = [...new Set(lists.map((l) => l.owner_id))];
  let owners = new Map();
  if (ownerIds.length) {
    const { data: profiles, error } = await gateway.rawQuery(gateway.TABLES.PUBLIC_PROFILES, {
      select: 'id,username,full_name',
      filters: { id: `in.(${ownerIds.join(',')})` },
      limit: ownerIds.length,
    });
    if (error) throw error;
    owners = new Map((profiles || []).map((p) => [p.id, p]));
  }
  return lists
    .map((l) => ({ ...l, owner: owners.get(l.owner_id) || null }))
    .filter((l) => l.owner);
}

/** Public lists strip for any profile page (owner sees all their lists there via My lists). */
/**
 * The admin-curated Featured shelf (2026-08-04): PUBLIC lists an admin
 * starred, owners joined for the byline/URL. Resolves [] until the
 * 20260804b_featured_lists.sql migration is applied (filtering on the
 * missing column 400s → caught), so the Discover door simply hides.
 */
/** Discover's door probe: is anything on the shelf? One row, no joins. */
export async function hasFeaturedLists() {
  try {
    return await cached('featured-any', async () => {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.LISTS, {
        select: 'id',
        filters: { featured: 'eq.true', visibility: 'eq.public' },
        limit: 1,
      });
      if (error) throw error;
      return (data || []).length > 0;
    });
  } catch {
    return false;
  }
}

export async function fetchFeaturedLists() {
  try {
    return await cached('featured-lists', async () => {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.LISTS, {
        select: LIST_SELECT,
        filters: { featured: 'eq.true', visibility: 'eq.public' },
        order: 'created_at.asc',
        limit: 50,
      });
      if (error) throw error;
      return joinOwners((data || []).map((l) => ({ ...l, entry_count: entryCount(l) })));
    });
  } catch {
    return [];
  }
}

export async function fetchPublicLists(ownerId) {
  return cached(`public-lists:${ownerId}`, async () => {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.LISTS, {
      select: LIST_SELECT,
      filters: { owner_id: `eq.${ownerId}`, visibility: 'eq.public' },
      order: 'created_at.asc',
      limit: 50,
    });
    if (error) throw error;
    return (data || []).map((l) => ({ ...l, entry_count: entryCount(l) }));
  });
}

// ---------------------------------------------------------------------------
// Category association (20260813f): a list can carry a site category, so
// a search for "Burger" surfaces "Top 16 Burgers in Los Angeles" next to
// the Burgers category, and the category page shows the lists tagged with
// it. PUBLIC lists only on every one of these surfaces — the sitemap /
// Featured-shelf rule: unlisted means "readable by whoever has the URL",
// and putting one in search would publish it. Everything here fails soft
// until the migration is applied by hand.
// ---------------------------------------------------------------------------

/**
 * Is the category column applied yet? Gates the pickers on the list
 * forms and the category-match half of search. Selecting the missing
 * column 400s → false; the miss isn't cached, so the first page load
 * after the migration lands answers true.
 */
export async function listsCategoryAvailable() {
  try {
    return await cached('lists-category-available', async () => {
      const { error } = await gateway.rawQuery(gateway.TABLES.LISTS, {
        select: 'category_id',
        limit: 1,
      });
      if (error) throw error;
      return true;
    });
  } catch {
    return false;
  }
}

const LIST_RESULT_LIMIT = 8;

/**
 * Public lists matching a search: by their own title/description, AND by
 * carrying any of the categories the same search matched (the caller
 * passes matchCategories' ids). Union, deduped, featured first — direct
 * title matches ahead of category-only ones. Both halves fail soft so a
 * pre-migration category filter can never sink the title matches.
 */
export async function fetchMatchingLists(search = '', categoryIds = []) {
  // PostgREST reserved characters would break the or=() syntax
  const q = search.replace(/[,()%_*\\]/g, ' ').trim();
  if (!q && !categoryIds.length) return [];
  const catKey = [...categoryIds].sort().join(',');
  try {
    return await cached(`list-search:${q.toLowerCase()}:${catKey}`, async () => {
      const readPublic = (filters) =>
        gateway.rawQuery(gateway.TABLES.LISTS, {
          select: LIST_SELECT,
          filters: { visibility: 'eq.public', ...filters },
          order: 'created_at.asc',
          limit: LIST_RESULT_LIMIT,
        }).then(({ data, error }) => {
          if (error) throw error;
          return data || [];
        }).catch(() => []);
      const [byText, byCategory] = await Promise.all([
        q ? readPublic({ or: `(title.ilike.*${q}*,description.ilike.*${q}*)` }) : [],
        categoryIds.length ? readPublic({ category_id: `in.(${categoryIds.join(',')})` }) : [],
      ]);
      const seen = new Set();
      const union = [...byText, ...byCategory].filter((l) => {
        if (seen.has(l.id)) return false;
        seen.add(l.id);
        return true;
      });
      const lists = union.slice(0, LIST_RESULT_LIMIT)
        .map((l) => ({ ...l, entry_count: entryCount(l) }))
        .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)); // stable — order within groups holds
      return joinOwners(lists);
    });
  } catch {
    return [];
  }
}

/** The category page's shelf: public lists carrying this category. */
export async function fetchCategoryLists(categoryId) {
  if (!categoryId) return [];
  try {
    return await cached(`category-lists:${categoryId}`, async () => {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.LISTS, {
        select: LIST_SELECT,
        filters: { category_id: `eq.${categoryId}`, visibility: 'eq.public' },
        order: 'created_at.asc',
        limit: 50,
      });
      if (error) throw error;
      return joinOwners((data || []).map((l) => ({ ...l, entry_count: entryCount(l) })));
    });
  } catch {
    return [];
  }
}
